import { jwtDecode } from 'jwt-decode';
import { buildQuery } from '@/core/routing/matrix';

export type GuardResult = { allow: true } | { allow: false; redirectTo: string };

interface GuardDeps {
  getAccessToken: () => Promise<string | undefined>;
  signOut: (navigate?: boolean) => void;
}

/**
 * Decision logic of Angular's AuthGuard, as a pure function of the requested URL (path + query), so it can be
 * tested without a router. It runs once when entering `/home` (and at `/`), not on every navigation.
 */
export async function evaluateAuthGuard(url: string, deps: GuardDeps): Promise<GuardResult> {
  const token = await deps.getAccessToken();

  if (!token) {
    // Remember where the user was going so the login page can send them back there.
    const returnUrl = url === '/' ? undefined : url;
    return { allow: false, redirectTo: `/login${buildQuery({ returnUrl })}` };
  }

  const decoded = jwtDecode<{ tenantName?: string }>(token);
  if (!decoded.tenantName) {
    console.error('The access token has no tenantName claim; the account is not set up for this application.');
    deps.signOut(false);
    return { allow: false, redirectTo: `/login${buildQuery({ reason: 'no-tenant' })}` };
  }

  // The site root lands on the dashboard.
  const path = url.split('?')[0];
  return path === '/' || path === '' ? { allow: false, redirectTo: '/home/dashboard' } : { allow: true };
}
